import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";

import {
  PriceModel,
  CreatePriceModelParams,
  UpdatePriceModelParams,
  ExecutePriceModelParams,
  ExecutePriceModelResponse,
  ListPriceModelsResponseSchema,
  GetPriceModelResponseSchema,
  CreatePriceModelResponseSchema,
  UpdatePriceModelResponseSchema,
  ExecutePriceModelResponseSchema,
} from "@forgepoint/shared";

interface PriceModelLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  isLoadingExecute: boolean;
  getError: string | null;
  saveError: string | null;
  deleteError: string | null;
  executeError: string | null;
}

interface PriceModelsStore {
  priceModels: Map<string, PriceModel>;
  priceModelLoadingStates: Map<string, PriceModelLoadingState>;
  listLoading: boolean;
  listError: string | null;

  getPriceModel: (id: string) => PriceModel | undefined;
  getPriceModelLoadingState: (id: string) => PriceModelLoadingState;
  fetchPriceModels: (params?: {
    includeArchived?: boolean;
  }) => Promise<string[]>;
  fetchPriceModel: (id: string) => Promise<void>;
  createPriceModel: (
    data: CreatePriceModelParams,
  ) => Promise<{ priceModel: PriceModel | null; error: string | null }>;
  updatePriceModel: (
    id: string,
    data: UpdatePriceModelParams,
  ) => Promise<{ priceModel: PriceModel | null; error: string | null }>;
  deletePriceModel: (
    id: string,
  ) => Promise<{ success: boolean; error?: string }>;
  executePriceModel: (
    id: string,
    params: ExecutePriceModelParams,
  ) => Promise<ExecutePriceModelResponse | null>;
}

const getInitialLoadingState = (): PriceModelLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  isLoadingExecute: false,
  getError: null,
  saveError: null,
  deleteError: null,
  executeError: null,
});

export const usePriceModelsStore = create<PriceModelsStore>((set, get) => ({
  priceModels: new Map(),
  priceModelLoadingStates: new Map(),
  listLoading: false,
  listError: null,

  getPriceModel: (id: string) => get().priceModels.get(id),

  getPriceModelLoadingState: (id: string) =>
    get().priceModelLoadingStates.get(id) || getInitialLoadingState(),

  fetchPriceModels: async (params?: { includeArchived?: boolean }) => {
    set(() => ({ listLoading: true, listError: null }));

    try {
      const searchParams = new URLSearchParams();
      if (params?.includeArchived) {
        searchParams.set("includeArchived", "true");
      }
      const queryString = searchParams.toString();
      const response = await fetchApi(
        queryString ? `price-models?${queryString}` : "price-models",
        "GET",
      );

      if (response.ok && response.data) {
        const data = ListPriceModelsResponseSchema.parse(response.data);
        set((state) => {
          const priceModelsMap = new Map(state.priceModels);
          data.priceModels.forEach((pm) => {
            priceModelsMap.set(pm.id, pm);
          });
          return { priceModels: priceModelsMap, listLoading: false };
        });
        return data.priceModels.map((pm) => pm.id);
      }

      set(() => ({
        listError: "Failed to fetch price models",
        listLoading: false,
      }));
      return [];
    } catch (error) {
      captureException(error as Error);
      set(() => ({
        listError: "Failed to fetch price models",
        listLoading: false,
      }));
      return [];
    }
  },

  fetchPriceModel: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.priceModelLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { priceModelLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`price-models/${id}`, "GET");

      if (response.ok && response.data) {
        const data = GetPriceModelResponseSchema.parse(response.data);
        set((state) => {
          const priceModelsMap = new Map(state.priceModels);
          const loadingStates = new Map(state.priceModelLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          priceModelsMap.set(id, data.priceModel);
          loadingStates.set(id, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });
          return {
            priceModels: priceModelsMap,
            priceModelLoadingStates: loadingStates,
          };
        });
        return;
      }

      set((state) => {
        const loadingStates = new Map(state.priceModelLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch price model",
        });
        return { priceModelLoadingStates: loadingStates };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.priceModelLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch price model",
        });
        return { priceModelLoadingStates: loadingStates };
      });
    }
  },

  createPriceModel: async (data: CreatePriceModelParams) => {
    try {
      const response = await fetchApi("price-models", "POST", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = CreatePriceModelResponseSchema.parse(response.data);
        set((state) => {
          const priceModelsMap = new Map(state.priceModels);
          priceModelsMap.set(result.priceModel.id, result.priceModel);
          return { priceModels: priceModelsMap };
        });
        return { priceModel: result.priceModel, error: null };
      }

      const errorMessage = response.error ?? "Failed to create price model";
      return { priceModel: null, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      return { priceModel: null, error: "Failed to create price model" };
    }
  },

  updatePriceModel: async (id: string, data: UpdatePriceModelParams) => {
    set((state) => {
      const loadingStates = new Map(state.priceModelLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { priceModelLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`price-models/${id}`, "PUT", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = UpdatePriceModelResponseSchema.parse(response.data);
        set((state) => {
          const priceModelsMap = new Map(state.priceModels);
          const loadingStates = new Map(state.priceModelLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          priceModelsMap.set(id, result.priceModel);
          loadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return {
            priceModels: priceModelsMap,
            priceModelLoadingStates: loadingStates,
          };
        });
        return { priceModel: result.priceModel, error: null };
      }

      const errorMessage = response.error ?? "Failed to update price model";
      set((state) => {
        const loadingStates = new Map(state.priceModelLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: errorMessage,
        });
        return { priceModelLoadingStates: loadingStates };
      });
      return { priceModel: null, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.priceModelLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update price model",
        });
        return { priceModelLoadingStates: loadingStates };
      });
      return { priceModel: null, error: "Failed to update price model" };
    }
  },

  deletePriceModel: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.priceModelLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingDelete: true,
        deleteError: null,
      });
      return { priceModelLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`price-models/${id}`, "DELETE");

      if (response.ok) {
        set((state) => {
          const priceModelsMap = new Map(state.priceModels);
          const loadingStates = new Map(state.priceModelLoadingStates);
          priceModelsMap.delete(id);
          loadingStates.delete(id);
          return {
            priceModels: priceModelsMap,
            priceModelLoadingStates: loadingStates,
          };
        });
        return { success: true };
      }

      const errorMessage =
        (response.data as { error?: string })?.error ||
        "Failed to delete price model";
      set((state) => {
        const loadingStates = new Map(state.priceModelLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: errorMessage,
        });
        return { priceModelLoadingStates: loadingStates };
      });
      return { success: false, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      const errorMessage = "Failed to delete price model";
      set((state) => {
        const loadingStates = new Map(state.priceModelLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: errorMessage,
        });
        return { priceModelLoadingStates: loadingStates };
      });
      return { success: false, error: errorMessage };
    }
  },

  executePriceModel: async (id: string, params: ExecutePriceModelParams) => {
    set((state) => {
      const loadingStates = new Map(state.priceModelLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingExecute: true,
        executeError: null,
      });
      return { priceModelLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`price-models/${id}/execute`, "POST", {
        body: params,
      });

      if (response.ok && response.data) {
        const result = ExecutePriceModelResponseSchema.parse(response.data);
        set((state) => {
          const loadingStates = new Map(state.priceModelLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          loadingStates.set(id, {
            ...currentState,
            isLoadingExecute: false,
            executeError: null,
          });
          return { priceModelLoadingStates: loadingStates };
        });
        return result;
      }

      const errorMessage =
        (response.data as { error?: string })?.error ||
        "Failed to execute price model";
      set((state) => {
        const loadingStates = new Map(state.priceModelLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingExecute: false,
          executeError: errorMessage,
        });
        return { priceModelLoadingStates: loadingStates };
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      const errorMessage = "Failed to execute price model";
      set((state) => {
        const loadingStates = new Map(state.priceModelLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingExecute: false,
          executeError: errorMessage,
        });
        return { priceModelLoadingStates: loadingStates };
      });
      return null;
    }
  },
}));
